/**
 * Hook para execução de testes de fluxo
 * Executa o fluxo localmente no navegador com chamadas reais de webhook
 * 
 * RELEASE: 1.1.0 - 2024-12-24
 * - Adicionada execução real de webhooks via Edge Function test-webhook
 * - Mapeamento de resposta para variáveis do fluxo
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { FlowNode, FlowEdge, normalizeNotificationRecipients, type SendNotificationConfig } from '@/types/flows';
import { createLogger } from '@/lib/logger';
import { validateInput } from '@/lib/input-validators';
import { supabase } from '@/integrations/supabase/client';
import { normalizeEmbedUrl } from '@/lib/embed-utils';
import { applyVariableFormat } from '@/lib/variableFormatter';

const log = createLogger('useFlowTestEngine');

export interface TestMessage {
  id: string;
  type: 'bot' | 'user' | 'system' | 'action' | 'delay';
  content: string;
  timestamp: Date;
  buttons?: Array<{ 
    id: string; 
    text: string;
    title?: string;
    description?: string;
  }>;
  metadata?: Record<string, any>;
}

/**
 * Extrai valor de um objeto usando path (suporta dot notation e array index)
 * Ex: "data.user.name", "[0].id", "items[0].title"
 * 
 * @param obj - Objeto de onde extrair o valor
 * @param path - Caminho para o valor (ex: "data.items[0].name")
 * @returns O valor encontrado ou undefined
 */
const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;
  
  // Normaliza path: "[0].campo" -> "0.campo", "campo[0]" -> "campo.0"
  const normalizedPath = path
    .replace(/\[(\d+)\]/g, '.$1') // [0] -> .0
    .replace(/^\./, ''); // Remove ponto inicial
  
  const parts = normalizedPath.split('.');
  let value = obj;
  
  for (const part of parts) {
    if (value === null || value === undefined) return undefined;
    
    // Tenta como índice numérico se for array
    const index = parseInt(part, 10);
    if (!isNaN(index) && Array.isArray(value)) {
      value = value[index];
    } else {
      value = value[part];
    }
  }
  
  return value;
};

/**
 * Props do hook com tenantId obrigatório para chamadas de webhook
 */
interface UseFlowTestEngineProps {
  tenantId?: string;
}

export function useFlowTestEngine(props?: UseFlowTestEngineProps) {
  const tenantId = props?.tenantId;
  
  const [messages, setMessages] = useState<TestMessage[]>([]);
  const [variables, setVariables] = useState<Record<string, any>>({});
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [isWaitingInput, setIsWaitingInput] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [edges, setEdges] = useState<FlowEdge[]>([]);

  // Refs para acessar valores sem causar re-renders
  const nodesRef = useRef<FlowNode[]>([]);
  const edgesRef = useRef<FlowEdge[]>([]);
  const variablesRef = useRef<Record<string, any>>({});

  // Sincroniza refs com estado
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);
  useEffect(() => { variablesRef.current = variables; }, [variables]);

  /**
   * Adiciona mensagem ao chat
   */
  const addMessage = useCallback((
    content: string,
    type: TestMessage['type'],
    metadata?: Record<string, any>
  ) => {
    const message: TestMessage = {
      id: `msg-${Date.now()}-${Math.random()}`,
      type,
      content,
      timestamp: new Date(),
      ...metadata,
    };
    setMessages(prev => [...prev, message]);
    log.debug('Mensagem adicionada:', message);
  }, []);

  /**
   * Substitui variáveis no texto
   */
  const replaceVariables = useCallback((text: string, vars: Record<string, any>) => {
    // Suporta sintaxe {{variavel||formato}} para formatação inline
    return text.replace(/\{\{(\w+(?:\.\w+)*)(?:\|\|([^}]+))?\}\}/g, (match, varName, fmt) => {
      const rawValue = vars[varName];
      if (rawValue === undefined) return match;
      // Aplica formatação se formato especificado
      if (fmt) {
        return applyVariableFormat(rawValue, fmt);
      }
      return String(rawValue);
    });
  }, []);

  /**
   * Encontra próximo node baseado nas edges
   * @param fromNodeId - ID do node atual
   * @param sourceHandle - Handle específico de saída (para nodes com múltiplas saídas como question_buttons)
   * @param edgeLabel - Label da edge (para condições true/false)
   */
  const findNextNode = useCallback((fromNodeId: string, sourceHandle?: string, edgeLabel?: string): FlowNode | null => {
    let targetEdge: FlowEdge | undefined;
    
    // Primeiro, tenta encontrar por sourceHandle (para question_buttons/question_list)
    if (sourceHandle) {
      targetEdge = edgesRef.current.find(e => 
        e.source === fromNodeId && e.sourceHandle === sourceHandle
      );
    }
    
    // Se não encontrou por sourceHandle, tenta por label (para condições)
    if (!targetEdge && edgeLabel) {
      targetEdge = edgesRef.current.find(e => 
        e.source === fromNodeId && e.label === edgeLabel
      );
    }
    
    // Fallback: procura qualquer edge saindo deste node
    if (!targetEdge) {
      targetEdge = edgesRef.current.find(e => e.source === fromNodeId);
    }

    if (!targetEdge) return null;

    return nodesRef.current.find(n => n.id === targetEdge!.target) || null;
  }, []);

  /**
   * Remove acentos de uma string
   */
  const removeAccents = useCallback((str: string): string => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }, []);

  /**
   * Normaliza valor baseado nas opções de correspondência
   */
  const normalizeValue = useCallback((value: string, options?: { ignoreCase?: boolean; ignoreAccents?: boolean; trimSpaces?: boolean }): string => {
    let result = value;
    if (options?.ignoreCase) result = result.toLowerCase();
    if (options?.ignoreAccents) result = removeAccents(result);
    if (options?.trimSpaces) result = result.trim().replace(/\s+/g, ' ');
    return result;
  }, [removeAccents]);

  /**
   * Avalia uma única condição com suporte a múltiplos valores e normalização
   */
  // Opções de normalização padrão quando não especificadas
  const DEFAULT_MATCH_OPTIONS = {
    ignoreCase: true,
    ignoreAccents: true,
    trimSpaces: true,
  };

  const evaluateSingleCondition = useCallback((cond: any, vars: Record<string, any>): boolean => {
    const { variable, operator, value, values, matchOptions } = cond;
    const varValue = vars[variable];
    
    // CORREÇÃO: Usar opções default se matchOptions não existir
    const effectiveMatchOptions = matchOptions || DEFAULT_MATCH_OPTIONS;

    log.debug('Avaliando condição:', { variable, operator, value, values, varValue, effectiveMatchOptions });

    // Lista de valores a verificar (prioriza values[], senão usa value)
    const valuesToCheck = (values && values.length > 0) ? values : (value ? [value] : []);
    
    // Normaliza o valor da variável
    const normalizedVarValue = normalizeValue(String(varValue || ''), effectiveMatchOptions);

    // Para operadores que não precisam de valor
    switch (operator) {
      case 'is_set':
        return varValue !== undefined && varValue !== null && varValue !== '';
      case 'is_empty':
        return varValue === undefined || varValue === null || varValue === '';
      // Operadores específicos para arrays
      case 'has_items':
        // Retorna true se for array com pelo menos 1 item
        return Array.isArray(varValue) && varValue.length > 0;
      case 'is_empty_array':
        // Retorna true se não for array ou se for array vazio
        return !Array.isArray(varValue) || varValue.length === 0;
    }

    // Para operadores numéricos, usa valor único
    if (['greater', 'less', 'greater_equal', 'less_equal', '>', '<', '>=', '<='].includes(operator)) {
      const numVar = Number(varValue);
      const numVal = Number(valuesToCheck[0] || 0);
      switch (operator) {
        case '>':
        case 'greater':
          return numVar > numVal;
        case '<':
        case 'less':
          return numVar < numVal;
        case '>=':
        case 'greater_equal':
          return numVar >= numVal;
        case '<=':
        case 'less_equal':
          return numVar <= numVal;
      }
    }

    // Para regex, usa valor único
    if (operator === 'regex') {
      try {
        return new RegExp(valuesToCheck[0] || '').test(String(varValue || ''));
      } catch {
        return false;
      }
    }

    // Para operadores de texto, verifica contra todos os valores
    const normalizedValues = valuesToCheck.map(v => normalizeValue(String(v || ''), effectiveMatchOptions));

    switch (operator) {
      case '==':
      case 'equals':
        return normalizedValues.some(nv => normalizedVarValue === nv);
      case '!=':
      case 'not_equals':
        return normalizedValues.every(nv => normalizedVarValue !== nv);
      case 'contains':
        return normalizedValues.some(nv => normalizedVarValue.includes(nv));
      case 'not_contains':
        return normalizedValues.every(nv => !normalizedVarValue.includes(nv));
      case 'starts_with':
        return normalizedValues.some(nv => normalizedVarValue.startsWith(nv));
      case 'ends_with':
        return normalizedValues.some(nv => normalizedVarValue.endsWith(nv));
      default:
        log.warn('Operador desconhecido:', operator);
        return false;
    }
  }, [normalizeValue]);

  /**
   * Avalia um bloco de comparação (IF) com múltiplas condições
   */
  const evaluateComparison = useCallback((comparison: any, vars: Record<string, any>): boolean => {
    const conditions = comparison.conditions || [];
    if (conditions.length === 0) return false;
    
    const results = conditions.map((cond: any) => evaluateSingleCondition(cond, vars));
    const logicOp = comparison.logicOperator || 'and';
    
    return logicOp === 'and' 
      ? results.every((r: boolean) => r) 
      : results.some((r: boolean) => r);
  }, [evaluateSingleCondition]);

  /**
   * Avalia condição legado (formato antigo com único IF)
   */
  const evaluateCondition = useCallback((config: any, vars: Record<string, any>): boolean => {
    // Novo formato: múltiplos IFs
    if (config.comparisons && Array.isArray(config.comparisons)) {
      // No formato legado, sempre retornava true/false
      // Aqui mantemos compatibilidade: se primeiro IF for true, retorna true
      for (const comparison of config.comparisons) {
        if (evaluateComparison(comparison, vars)) {
          return true;
        }
      }
      return false;
    }
    
    // Formato legado: conditions + logicOperator
    if (config.conditions && Array.isArray(config.conditions)) {
      const results = config.conditions.map((cond: any) => evaluateSingleCondition(cond, vars));
      const logicOp = config.logicOperator || 'and';
      return logicOp === 'and' ? results.every((r: boolean) => r) : results.some((r: boolean) => r);
    }
    
    // Formato legado mais antigo: variable, operator, value diretos
    return evaluateSingleCondition(config, vars);
  }, [evaluateSingleCondition, evaluateComparison]);

  /**
   * Move para o próximo node
   */
  const moveToNextNode = useCallback((fromNodeId: string, vars: Record<string, any>, sourceHandle?: string, edgeLabel?: string) => {
    const nextNode = findNextNode(fromNodeId, sourceHandle, edgeLabel);
    
    if (nextNode) {
      log.debug('Movendo para próximo node: ' + nextNode.id, { sourceHandle, edgeLabel });
      processTestNodeRef.current?.(nextNode, vars);
    } else {
      log.debug('Nenhum próximo node encontrado, finalizando');
      addMessage('Fluxo finalizado (sem próximo node)', 'system');
      setIsRunning(false);
      setHighlightedNodeId(null);
    }
  }, [findNextNode, addMessage]);

  // Ref para evitar dependência circular
  const processTestNodeRef = useRef<(node: FlowNode, vars: Record<string, any>) => void>();

  /**
   * Processa um node baseado no seu tipo
   */
  const processTestNode = useCallback(async (node: FlowNode, vars: Record<string, any>) => {
    log.debug('Processando node: ' + node.data.type, { nodeId: node.id });
    setCurrentNodeId(node.id);
    setHighlightedNodeId(node.id);

    const config = node.data.config || {};

    switch (node.data.type) {
      case 'start':
        addMessage('Fluxo iniciado', 'system');
        setTimeout(() => moveToNextNode(node.id, vars), 500);
        break;

      case 'message_text':
        const textContent = replaceVariables(config.text || 'Mensagem não configurada', vars);
        addMessage(textContent, 'bot');
        setTimeout(() => moveToNextNode(node.id, vars), 1000);
        break;

      case 'message_media':
        const mediaCaption = replaceVariables(config.caption || '', vars);
        addMessage(mediaCaption, 'bot', {
          metadata: { 
            mediaType: config.media_type,
            mediaUrl: config.media_url,
            caption: mediaCaption,
            mimeType: config.mime_type,
            filename: config.filename
          }
        });
        setTimeout(() => moveToNextNode(node.id, vars), 1000);
        break;

      /**
       * Processa node de carrossel (message_carousel)
       * Exibe mensagem principal + preview dos cartões no chat de teste
       * 
       * RELEASE: 1.0.0 - 2026-02-08 - Novo node de carrossel multi-provider
       */
      case 'message_carousel': {
        const carouselMessage = replaceVariables(config.message || '', vars);
        const cards = config.cards || [];
        
        // Monta descrição dos cartões para exibição no chat de teste
        const cardsSummary = cards.length > 0 
          ? `\n\n📦 ${cards.length} cartão${cards.length > 1 ? 'ões' : ''} configurado${cards.length > 1 ? 's' : ''}:`
          : '';
        
        // Monta preview de cada cartão
        const cardsPreview = cards.map((card: any, index: number) => {
          const cardText = replaceVariables(card.text || '', vars);
          const buttonsCount = card.buttons?.length || 0;
          return `  🖼️ Cartão ${index + 1}: ${cardText.substring(0, 50)}${cardText.length > 50 ? '...' : ''}${buttonsCount > 0 ? ` (${buttonsCount} botão${buttonsCount > 1 ? 'ões' : ''})` : ''}`;
        }).join('\n');
        
        addMessage(
          (carouselMessage || '[Carrossel sem mensagem principal]') + cardsSummary + (cardsPreview ? '\n' + cardsPreview : ''), 
          'bot', 
          {
            metadata: { 
              type: 'carousel',
              cardCount: cards.length,
              cards: cards.map((card: any) => ({
                text: replaceVariables(card.text || '', vars),
                imageUrl: card.image_url,
                buttons: card.buttons?.map((btn: any) => btn.label),
              })),
            },
          }
        );
        
        setTimeout(() => moveToNextNode(node.id, vars), 1000);
        break;
      }

      case 'question_buttons':
        const questionText = replaceVariables(config.text || 'Escolha uma opção:', vars);
        const buttons = config.options?.map((opt: any, index: number) => ({
          id: opt.id || String(index),
          text: opt.label
        })) || [];
        addMessage(questionText, 'bot', { buttons });
        setIsWaitingInput(true);
        break;

      case 'question_list':
        /**
         * Processa node de lista de opções (dinâmica ou estática)
         * 
         * RELEASE: 2.2.0 - 2024-12-28 - Adicionado suporte a options_array_path
         * Permite navegar até arrays aninhados em qualquer profundidade
         */
        const listQuestion = replaceVariables(config.text || 'Escolha uma opção:', vars);
        
        let listOptions: Array<{id: string, text: string, title?: string, description?: string}> = [];
        
        // Verifica se as opções vêm de uma variável (lista dinâmica)
        if (config.options_source === 'variable' && config.options_variable) {
          // Busca o valor da variável
          let sourceData = vars[config.options_variable];
          
          // NOVO: Se tem array_path, navega até o array dentro do objeto
          if (config.options_array_path && sourceData && typeof sourceData === 'object') {
            sourceData = getNestedValue(sourceData, config.options_array_path);
            log.info(`[question_list] Navegou para array em path: ${config.options_array_path}`);
          }
          
          if (Array.isArray(sourceData) && sourceData.length > 0) {
            const labelPath = config.options_label_path || 'label';
            const valuePath = config.options_value_path || labelPath;
            const descriptionPath = config.options_description_path;
            
            const mappedOptions = sourceData.map((item: any, index: number) => {
              const label = getNestedValue(item, labelPath) || `Item ${index + 1}`;
              const value = getNestedValue(item, valuePath) || label;
              const description = descriptionPath ? getNestedValue(item, descriptionPath) : undefined;
              
              return {
                id: String(value),
                text: String(label),
                title: String(label),
                description: description ? String(description) : undefined
              };
            });
            
            // Aplica distinct se configurado (padrão: true - remove duplicatas)
            if (config.options_distinct !== false) {
              const uniqueMap = new Map<string, typeof mappedOptions[0]>();
              mappedOptions.forEach(opt => {
                if (!uniqueMap.has(opt.id)) {
                  uniqueMap.set(opt.id, opt);
                }
              });
              listOptions = Array.from(uniqueMap.values());
              log.debug(`Opções após distinct: ${listOptions.length} únicos de ${mappedOptions.length} totais`);
            } else {
              listOptions = mappedOptions;
              log.debug(`Opções dinâmicas carregadas: ${listOptions.length} itens (distinct desabilitado)`);
            }
          } else {
            const pathInfo = config.options_array_path 
              ? ` (path: ${config.options_array_path})`
              : '';
            log.warn(`Variável de opções vazia ou não é array: ${config.options_variable}${pathInfo}`);
            addMessage(`⚠️ Nenhuma opção disponível na variável: ${config.options_variable}${pathInfo}`, 'system');
          }
        } else {
          // Opções estáticas (comportamento original)
          listOptions = config.options?.map((opt: any, index: number) => ({
            id: opt.id || String(index),
            text: opt.label || opt.title,
            title: opt.title,
            description: opt.description
          })) || [];
        }
        
        addMessage(listQuestion, 'bot', { buttons: listOptions });
        setIsWaitingInput(true);
        break;

      case 'input_data':
        const inputPrompt = replaceVariables(config.question || 'Digite sua resposta:', vars);
        addMessage(inputPrompt, 'bot');
        setIsWaitingInput(true);
        break;

      case 'condition':
        // Novo formato: múltiplos IFs + Else
        const comparisons = config.comparisons || [];
        
        // Se não tem comparisons, tenta formato legado
        if (comparisons.length === 0) {
          const legacyResult = evaluateCondition(config, vars);
          addMessage(
            `Condição avaliada: ${legacyResult ? 'Verdadeiro' : 'Falso'}`,
            'system',
            { metadata: { condition: config, result: legacyResult } }
          );
          setTimeout(() => {
            const sourceHandle = legacyResult ? 'true' : 'false';
            moveToNextNode(node.id, vars, sourceHandle);
          }, 800);
          break;
        }
        
        // Avalia cada IF em ordem
        let matchedIfIndex = -1;
        for (let i = 0; i < comparisons.length; i++) {
          const ifResult = evaluateComparison(comparisons[i], vars);
          if (ifResult) {
            matchedIfIndex = i;
            break;
          }
        }
        
        if (matchedIfIndex >= 0) {
          addMessage(
            `Condição IF #${matchedIfIndex + 1} verdadeira`,
            'system',
            { metadata: { matchedIf: matchedIfIndex } }
          );
          setTimeout(() => {
            moveToNextNode(node.id, vars, `if_${matchedIfIndex}`);
          }, 800);
        } else {
          addMessage(
            'Nenhum IF satisfeito, seguindo para Else',
            'system',
            { metadata: { matchedIf: 'else' } }
          );
          setTimeout(() => {
            moveToNextNode(node.id, vars, 'else');
          }, 800);
        }
        break;

      case 'delay':
        const duration = config.duration || 1;
        const unit = config.unit || 'seconds';
        
        // Calcula tempo real em ms
        const delayMs = duration * (
          unit === 'seconds' ? 1000 : 
          unit === 'minutes' ? 60000 : 
          unit === 'hours' ? 3600000 : 
          86400000
        );
        
        // Para teste, limita a 30 segundos máximo (não reduz, apenas limita)
        // Delays maiores que 30s mostram opção de pular
        const effectiveDelayMs = Math.min(delayMs, 30000);
        const canSkip = delayMs > effectiveDelayMs;
        
        // Formata unidade em português
        const unitLabel = {
          seconds: 'segundos',
          minutes: 'minutos', 
          hours: 'horas',
          days: 'dias'
        }[unit] || unit;
        
        addMessage(`Aguardando ${duration} ${unitLabel}...`, 'delay', {
          metadata: {
            delayMs: effectiveDelayMs,
            originalDelayMs: delayMs,
            canSkip,
            nodeId: node.id
          }
        });
        
        setTimeout(() => moveToNextNode(node.id, vars), effectiveDelayMs);
        break;

      case 'add_tag':
        addMessage(`Tag "${config.tag_name}" adicionada ao contato`, 'action');
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;

      case 'assign_phase':
        const phaseCatName = config.category_name || 'Categoria';
        const phaseNameVal = config.phase_name || 'Fase';
        vars['phase_name'] = phaseNameVal;
        vars['category_name'] = phaseCatName;
        setVariables({ ...vars });
        addMessage(`Fase definida: ${phaseCatName} → ${phaseNameVal}`, 'action');
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;

      case 'update_contact':
        const fields = Object.entries(config.fields || {}).map(([k, v]) => `${k}: ${v}`).join(', ');
        addMessage(`Contato atualizado: ${fields}`, 'action');
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;

      case 'transfer_human':
        // Adiciona agent_name às variáveis para substituição em mensagens subsequentes
        vars['agent_name'] = config.agent_name || '';
        setVariables({ ...vars });
        addMessage(`Conversa transferida para ${config.agent_name}`, 'action');
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;

      case 'transfer_department':
        // Adiciona department_name às variáveis para substituição em mensagens subsequentes
        vars['department_name'] = config.department_name || '';
        setVariables({ ...vars });
        addMessage(`Conversa transferida para departamento ${config.department_name}`, 'action');
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;

      case 'webhook':
        /**
         * Executa webhook REAL via Edge Function test-webhook
         * 
         * RELEASE: 1.2.0 - 2025-12-28
         * - Adicionada interpolação de variáveis nos HEADERS
         * - Adicionada detecção de variáveis não resolvidas
         * - URL exibida no chat agora mostra valor processado
         */
        (async () => {
          try {
            // Valida se temos tenantId para fazer a chamada
            if (!tenantId) {
              log.warn('[webhook] tenantId não disponível, simulando resposta');
              addMessage(`⚠️ Webhook simulado (sem tenantId): ${config.url}`, 'action');
              setTimeout(() => moveToNextNode(node.id, vars), 800);
              return;
            }
            
            // Substitui variáveis na URL
            const processedUrl = replaceVariables(config.url || '', vars);
            
            // Substitui variáveis no body se existir
            let processedBody = config.body;
            if (processedBody && typeof processedBody === 'string') {
              processedBody = replaceVariables(processedBody, vars);
            } else if (processedBody && typeof processedBody === 'object') {
              processedBody = replaceVariables(JSON.stringify(processedBody), vars);
            }
            
            // NOVO: Substitui variáveis nos HEADERS
            let processedHeaders: Record<string, string> = {};
            if (config.headers && typeof config.headers === 'object') {
              try {
                // Serializa headers, interpola variáveis, deserializa
                const headersStr = JSON.stringify(config.headers);
                const processedHeadersStr = replaceVariables(headersStr, vars);
                processedHeaders = JSON.parse(processedHeadersStr);
                log.info('[webhook] Headers processados com interpolação');
              } catch (parseErr) {
                log.warn('[webhook] Erro ao processar headers, usando originais:', parseErr);
                processedHeaders = config.headers as Record<string, string>;
              }
            }
            
            // NOVO: Detecta variáveis não resolvidas (padrão {{variavel}})
            const unresolvedPattern = /\{\{(\w+)\}\}/g;
            const unresolvedVars: string[] = [];
            
            const checkUnresolved = (value: string, source: string) => {
              let match;
              while ((match = unresolvedPattern.exec(value)) !== null) {
                unresolvedVars.push(`${source}: {{${match[1]}}}`);
              }
            };
            
            checkUnresolved(processedUrl, 'URL');
            if (processedBody && typeof processedBody === 'string') {
              checkUnresolved(processedBody, 'Body');
            }
            checkUnresolved(JSON.stringify(processedHeaders), 'Headers');
            
            // Alerta sobre variáveis não resolvidas
            if (unresolvedVars.length > 0) {
              log.warn('[webhook] Variáveis não resolvidas:', unresolvedVars);
              addMessage(`⚠️ Variáveis não resolvidas: ${unresolvedVars.join(', ')}`, 'system');
            }
            
            // Exibe URL PROCESSADA (não original) no chat
            addMessage(`🔄 Chamando webhook: ${processedUrl}`, 'action');
            
            log.info('[webhook] URL processada:', processedUrl);
            log.info('[webhook] Body processado:', processedBody);
            log.info('[webhook] Headers processados (keys):', Object.keys(processedHeaders));
            
            // Faz a chamada real via Edge Function test-webhook
            const { data, error } = await supabase.functions.invoke('test-webhook', {
              body: {
                url: processedUrl,
                method: config.method || 'GET',
                headers: processedHeaders,
                body: processedBody,
                credential_id: config.credential_id,
                tenant_id: tenantId
              }
            });
            
            if (error) {
              log.error('[webhook] Erro na Edge Function:', error);
              addMessage(`❌ Erro no webhook: ${error.message}`, 'system');
              setTimeout(() => moveToNextNode(node.id, vars), 800);
              return;
            }
            
            log.info('[webhook] Resposta recebida:', data);
            
            // Verifica se a resposta foi bem-sucedida
            if (!data.success) {
              addMessage(`⚠️ Webhook retornou erro: ${data.error || 'Status ' + data.statusCode}`, 'system');
              setTimeout(() => moveToNextNode(node.id, vars), 800);
              return;
            }
            
            const responseData = data.body;
            const webhookVars = { ...vars };
            let mappedCount = 0;
            
            // Aplica os mapeamentos de resposta configurados no node
            if (config.responseMapping && Array.isArray(config.responseMapping)) {
              for (const mapping of config.responseMapping) {
                const extractedValue = getNestedValue(responseData, mapping.responsePath);
                if (extractedValue !== undefined) {
                  webhookVars[mapping.variableName] = extractedValue;
                  mappedCount++;
                  log.debug(`[webhook] Mapeado: ${mapping.variableName} = ${extractedValue}`);
                } else {
                  log.warn(`[webhook] Path não encontrado: ${mapping.responsePath}`);
                }
              }
            }
            
            // Salva resposta completa em variável se configurado
            if (config.responseVariable) {
              webhookVars[config.responseVariable] = responseData;
              log.debug(`[webhook] Resposta completa salva em: ${config.responseVariable}`);
            }
            
            // Atualiza variáveis do fluxo
            setVariables(webhookVars);
            
            addMessage(
              `✅ Webhook executado (${data.statusCode}) - ${mappedCount} variável(eis) mapeada(s)`, 
              'action',
              { metadata: { response: responseData, mappedVars: Object.keys(webhookVars) } }
            );
            
            log.info('[webhook] Variáveis após mapeamento:', webhookVars);
            
            // Continua para próximo node com variáveis atualizadas
            setTimeout(() => moveToNextNode(node.id, webhookVars), 800);
            
          } catch (err) {
            log.error('[webhook] Exceção ao executar:', err);
            addMessage(`❌ Erro inesperado no webhook: ${err instanceof Error ? err.message : 'Erro desconhecido'}`, 'system');
            setTimeout(() => moveToNextNode(node.id, vars), 800);
          }
        })();
        break;

      /**
       * Node Definir Variável - atribui valor a uma variável do fluxo
       * Suporta valores estáticos, referência a outra variável, expressões e datas dinâmicas
       * 
       * RELEASE: 1.1.0 - 2025-12-28 - Adicionado suporte a value_type 'dynamic_date'
       */
      case 'set_variable':
        // Importação dinâmica para evitar circular dependency
        import('@/lib/dynamic-date').then(({ calculateDynamicDate }) => {
          const setVarName = config.variable_name || 'variavel';
          const setValueType = config.value_type || 'static';
          let setValue = config.value || '';
          
          // Processa valor baseado no tipo
          if (setValueType === 'dynamic_date' && config.date_config) {
            // NOVO: Calcula data dinâmica
            setValue = calculateDynamicDate(config.date_config);
            log.info(`[set_variable] Data dinâmica calculada: ${setValue}`);
          } else if (setValueType === 'variable') {
            const sourceVarName = setValue.replace(/\{\{|\}\}/g, '').trim();
            setValue = vars[sourceVarName] !== undefined ? vars[sourceVarName] : setValue;
          } else if (setValueType === 'expression') {
            setValue = replaceVariables(setValue, vars);
          }
          
          // Atualiza variável
          const updatedVars = { ...vars, [setVarName]: setValue };
          setVariables(updatedVars);
          
          addMessage(`Variável '${setVarName}' definida: ${setValue}`, 'action');
          setTimeout(() => moveToNextNode(node.id, updatedVars), 800);
        }).catch((err) => {
          log.error('[set_variable] Erro ao importar dynamic-date:', err);
          addMessage(`❌ Erro ao processar data dinâmica`, 'system');
          setTimeout(() => moveToNextNode(node.id, vars), 800);
        });
        break;

      case 'goto':
        const gotoTargetId = config.target_node_id;
        
        if (!gotoTargetId) {
          addMessage('❌ Node "Pular" sem destino configurado', 'system');
          setIsRunning(false);
          setHighlightedNodeId(null);
          break;
        }
        
        // Busca o node destino diretamente pelo ID (não usa edges)
        const gotoTargetNode = nodesRef.current.find(n => n.id === gotoTargetId);
        
        if (!gotoTargetNode) {
          addMessage(`❌ Node destino "${gotoTargetId}" não encontrado`, 'system');
          setIsRunning(false);
          setHighlightedNodeId(null);
          break;
        }
        
        addMessage(`↪️ Pulando para node: ${gotoTargetId}`, 'action');
        setTimeout(() => processTestNodeRef.current?.(gotoTargetNode, vars), 800);
        break;

      // Node Teste A/B - distribui fluxo por porcentagem
      case 'ab_test':
        const abVariants = config.variants || [
          { id: 'variant_0', label: 'A', percentage: 50 },
          { id: 'variant_1', label: 'B', percentage: 50 },
        ];
        
        // Gera número aleatório 0-100
        const randomValue = Math.random() * 100;
        let accum = 0;
        let selectedIdx = 0;
        
        // Seleciona variante baseado em distribuição acumulada
        for (let i = 0; i < abVariants.length; i++) {
          accum += abVariants[i].percentage;
          if (randomValue <= accum) {
            selectedIdx = i;
            break;
          }
        }
        
        const selectedVariant = abVariants[selectedIdx];
        addMessage(
          `🎲 Teste A/B: Selecionado **${selectedVariant.label}** (${selectedVariant.percentage}%)`,
          'system',
          { metadata: { randomValue: randomValue.toFixed(2), selectedVariant } }
        );
        
        setTimeout(() => moveToNextNode(node.id, vars, `variant_${selectedIdx}`), 800);
        break;

      /**
       * Node Horário Comercial - verifica se está no horário de atendimento
       * Possui duas saídas: 'open' (aberto) e 'closed' (fechado)
       * 
       * RELEASE: 1.0.0 - 2025-01-22 - Novo node
       */
      case 'business_hours':
        (() => {
          const bhConfig = config;
          const bhTimezone = bhConfig?.timezone || 'America/Sao_Paulo';
          const bhSchedule = bhConfig?.schedule;
          
          if (!bhSchedule) {
            addMessage('⚠️ Horário comercial não configurado', 'system');
            setTimeout(() => moveToNextNode(node.id, vars, 'closed'), 800);
            return;
          }
          
          // Obtém data/hora atual no fuso configurado
          const now = new Date();
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: bhTimezone,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            weekday: 'long',
          });
          
          const parts = formatter.formatToParts(now);
          const weekday = parts.find(p => p.type === 'weekday')?.value?.toLowerCase();
          const hour = parts.find(p => p.type === 'hour')?.value || '00';
          const minute = parts.find(p => p.type === 'minute')?.value || '00';
          const currentTime = `${hour}:${minute}`;
          
          // Mapeamento de weekday para chave do schedule
          const dayKeyMap: Record<string, string> = {
            'sunday': 'sunday',
            'monday': 'monday',
            'tuesday': 'tuesday',
            'wednesday': 'wednesday',
            'thursday': 'thursday',
            'friday': 'friday',
            'saturday': 'saturday',
          };
          
          const dayKey = dayKeyMap[weekday || 'monday'];
          const dayConfig = bhSchedule[dayKey as keyof typeof bhSchedule];
          
          // Verifica se está dentro do horário
          let isOpen = false;
          
          if (dayConfig?.enabled) {
            const startTime = dayConfig.start_time || '00:00';
            const endTime = dayConfig.end_time || '23:59';
            
            // Compara strings HH:mm (funciona para ordenação lexicográfica)
            isOpen = currentTime >= startTime && currentTime < endTime;
          }
          
          const resultLabel = isOpen ? 'ABERTO' : 'FECHADO';
          log.info(`[business_hours] timezone=${bhTimezone}, day=${weekday}, time=${currentTime}, isOpen=${isOpen}`);
          
          addMessage(
            `🕐 Horário comercial: ${resultLabel} (${weekday} ${currentTime} - ${bhTimezone})`,
            'system',
            { metadata: { timezone: bhTimezone, weekday, currentTime, isOpen } }
          );
          
          const sourceHandle = isOpen ? 'open' : 'closed';
          setTimeout(() => moveToNextNode(node.id, vars, sourceHandle), 800);
        })();
        break;

      case 'end':
        addMessage('Fluxo finalizado', 'system');
        setIsRunning(false);
        setHighlightedNodeId(null);
        break;

      /**
       * Node Filtrar Array - filtra itens de um array que satisfazem condição
       * RELEASE: 1.0.0 - 2024-12-24 - Novo node para filtrar arrays
       */
      case 'filter_array':
        (async () => {
          const {
            source_variable,    // ex: "boletos"
            field_path,         // ex: "status"
            operator: filterOp, // ex: "equals"
            filter_value,       // ex: "A"
            output_variable     // ex: "boletos_abertos"
          } = config;
          
          // Valida configuração
          if (!source_variable || !output_variable) {
            addMessage('⚠️ Filtrar Array não configurado corretamente', 'system');
            setTimeout(() => moveToNextNode(node.id, vars), 800);
            return;
          }
          
          const sourceArray = vars[source_variable];
          
          // Verifica se é um array
          if (!Array.isArray(sourceArray)) {
            addMessage(
              `⚠️ A variável "${source_variable}" não é um array (tipo: ${typeof sourceArray})`,
              'system'
            );
            // Salva array vazio na saída
            const newVars = { ...vars, [output_variable]: [] };
            setVariables(newVars);
            setTimeout(() => moveToNextNode(node.id, newVars), 800);
            return;
          }
          
          log.info(`[filter_array] Filtrando ${sourceArray.length} itens onde ${field_path} ${filterOp} "${filter_value}"`);
          
          // Função para avaliar condição em cada item
          const evaluateItemCondition = (item: any): boolean => {
            const itemValue = getNestedValue(item, field_path);
            const itemStr = String(itemValue || '').toLowerCase().trim();
            const filterStr = String(filter_value || '').toLowerCase().trim();
            
            switch (filterOp) {
              case 'equals':
                return itemStr === filterStr;
              case 'not_equals':
                return itemStr !== filterStr;
              case 'contains':
                return itemStr.includes(filterStr);
              case 'not_contains':
                return !itemStr.includes(filterStr);
              case 'starts_with':
                return itemStr.startsWith(filterStr);
              case 'ends_with':
                return itemStr.endsWith(filterStr);
              case 'greater':
                return Number(itemValue) > Number(filter_value);
              case 'less':
                return Number(itemValue) < Number(filter_value);
              case 'is_set':
                return itemValue !== undefined && itemValue !== null && itemValue !== '';
              case 'is_empty':
                return itemValue === undefined || itemValue === null || itemValue === '';
              default:
                log.warn(`[filter_array] Operador desconhecido: ${filterOp}`);
                return false;
            }
          };
          
          // Filtra TODOS os itens que satisfazem a condição
          const filteredArray = sourceArray.filter(item => evaluateItemCondition(item));
          
          log.info(`[filter_array] Resultado: ${filteredArray.length} de ${sourceArray.length} itens passaram no filtro`);
          
          // Atualiza variáveis
          const newVars = { ...vars, [output_variable]: filteredArray };
          setVariables(newVars);
          
          addMessage(
            `🔍 Filtrado: ${filteredArray.length} de ${sourceArray.length} itens (${field_path} ${filterOp} "${filter_value}")`,
            'action',
            { metadata: { sourceCount: sourceArray.length, filteredCount: filteredArray.length, outputVariable: output_variable } }
          );
          
          setTimeout(() => moveToNextNode(node.id, newVars), 800);
        })();
        break;

      /**
       * Node Formatar Array - transforma array em texto formatado
       * RELEASE: 1.0.0 - 2024-12-24 - Novo node para formatar arrays em mensagens
       * RELEASE: 2.0.0 - 2025-04-01 - Suporte a send_documents no motor de teste
       */
      case 'format_array':
        (async () => {
          const {
            source_variable,    // ex: "boletos_abertos"
            output_variable,    // ex: "lista_formatada"
            item_template,      // ex: "📄 Boleto {{$index}}\nCódigo: {{codigo_barras}}"
            separator = '\n\n', // Separador entre itens
            filter_field,       // Campo para filtrar (opcional)
            filter_value,       // Valor do filtro (pode usar variável)
            header_template,    // Texto antes da lista
            footer_template,    // Texto depois da lista
            empty_message = 'Nenhum item encontrado.',
            // Campos para envio de documentos
            send_documents,         // Se true, envia cada documento como mensagem separada
            document_url_field,     // Campo do item que contém a URL do documento
            document_caption_template,  // Template para legenda do documento
            document_filename_template  // Template para nome do arquivo
          } = config;
          
          // Valida configuração
          if (!source_variable || !output_variable || !item_template) {
            addMessage('⚠️ Formatar Array não configurado corretamente', 'system');
            setTimeout(() => moveToNextNode(node.id, vars), 800);
            return;
          }
          
          let sourceArray = vars[source_variable];
          
          // Verifica se é um array
          if (!Array.isArray(sourceArray)) {
            addMessage(
              `⚠️ A variável "${source_variable}" não é um array (tipo: ${typeof sourceArray})`,
              'system'
            );
            // Salva mensagem de vazio na saída
            const newVars = { ...vars, [output_variable]: empty_message };
            setVariables(newVars);
            setTimeout(() => moveToNextNode(node.id, newVars), 800);
            return;
          }
          
          // Aplica filtro se configurado
          if (filter_field && filter_value) {
            // Substitui variáveis no valor do filtro
            const resolvedFilterValue = replaceVariables(filter_value, vars);
            
            sourceArray = sourceArray.filter((item: any) => {
              const itemValue = getNestedValue(item, filter_field);
              return String(itemValue || '').toLowerCase().trim() === 
                     String(resolvedFilterValue || '').toLowerCase().trim();
            });
            
            log.debug(`[format_array] Após filtro (${filter_field} = ${resolvedFilterValue}): ${sourceArray.length} itens`);
          }
          
          // Verifica se array está vazio após filtro
          if (sourceArray.length === 0) {
            const resolvedEmptyMessage = replaceVariables(empty_message, vars);
            const newVars = { ...vars, [output_variable]: resolvedEmptyMessage };
            setVariables(newVars);
            addMessage(`📝 Array vazio - "${output_variable}" definida com mensagem padrão`, 'action');
            setTimeout(() => moveToNextNode(node.id, newVars), 800);
            return;
          }
          
          // Processa o separador (converte \n literal para quebra de linha)
          const processedSeparator = separator.replace(/\\n/g, '\n');
          
          // Formata cada item usando o template
          const formattedItems = sourceArray.map((item: any, index: number) => {
            let text = item_template
              // Substitui {{$index}} pelo número da linha (1-based)
              .replace(/\{\{\$index\}\}/g, String(index + 1))
              // Substitui {{$count}} pelo total de itens
              .replace(/\{\{\$count\}\}/g, String(sourceArray.length))
              // Substitui {{campo}} pelos valores do item
              // Suporta {{campo||formato}} para formatação inline de campos do item
              .replace(/\{\{(\w+(?:\.\w+)*)(?:\|\|([^}]+))?\}\}/g, (_match: string, fieldPath: string, fmt?: string) => {
                const value = getNestedValue(item, fieldPath);
                if (value === undefined || value === null) return '';
                // Aplica formatação se formato especificado
                if (fmt) {
                  return applyVariableFormat(value, fmt);
                }
                return String(value);
              });
            
            // Processa quebras de linha
            return text.replace(/\\n/g, '\n');
          });
          
          // Monta resultado final
          let result = '';
          
          // Adiciona header se configurado
          if (header_template) {
            const processedHeader = replaceVariables(header_template, vars)
              .replace(/\{\{\$count\}\}/g, String(sourceArray.length))
              .replace(/\\n/g, '\n');
            result += processedHeader + '\n\n';
          }
          
          // Junta os itens formatados
          result += formattedItems.join(processedSeparator);
          
          // Adiciona footer se configurado
          if (footer_template) {
            const processedFooter = replaceVariables(footer_template, vars)
              .replace(/\{\{\$count\}\}/g, String(sourceArray.length))
              .replace(/\\n/g, '\n');
            result += '\n\n' + processedFooter;
          }
          
          // =====================================================================
          // Modo send_documents: envia cada documento como mensagem separada
          // Simula o comportamento de produção no chat de teste
          // =====================================================================
          if (send_documents && document_url_field) {
            log.info(`[format_array] Modo send_documents ativo - enviando ${sourceArray.length} documento(s)`);
            
            // Função auxiliar para substituir campos do item no template
            const replaceItemFields = (template: string, item: any, index: number): string => {
              if (!template) return '';
              return template
                .replace(/\{\{\$index\}\}/g, String(index + 1))
                .replace(/\{\{\$count\}\}/g, String(sourceArray.length))
                .replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (_m: string, fp: string) => {
                  const val = getNestedValue(item, fp);
                  return val !== undefined && val !== null ? String(val) : '';
                })
                .replace(/\\n/g, '\n');
            };
            
            // Envia cada documento com delay entre eles
            for (let i = 0; i < sourceArray.length; i++) {
              const item = sourceArray[i];
              const documentUrl = getNestedValue(item, document_url_field);
              
              if (!documentUrl) {
                log.warn(`[format_array] Item ${i + 1}: campo "${document_url_field}" vazio, pulando`);
                continue;
              }
              
              // Gera legenda usando template ou usa o texto formatado do item
              const caption = document_caption_template 
                ? replaceItemFields(document_caption_template, item, i)
                : formattedItems[i] || '';
              
              // Gera nome do arquivo usando template
              const filename = document_filename_template
                ? replaceItemFields(document_filename_template, item, i)
                : `documento_${i + 1}.pdf`;
              
              // Adiciona mensagem com preview de documento no chat de teste
              addMessage(caption || filename, 'bot', {
                metadata: {
                  mediaType: 'document',
                  mediaUrl: String(documentUrl),
                  caption: caption,
                  filename: filename
                }
              });
              
              log.debug(`[format_array] Documento ${i + 1}/${sourceArray.length} enviado: ${filename}`);
              
              // Delay entre documentos para simular envio sequencial
              if (i < sourceArray.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            }
            
            // Salva resumo na variável de saída
            const summary = `${sourceArray.length} documento(s) enviado(s)`;
            const newVars = { ...vars, [output_variable]: summary };
            setVariables(newVars);
            
            addMessage(
              `📎 ${sourceArray.length} documento(s) enviado(s) - resumo salvo em "${output_variable}"`,
              'action',
              { metadata: { sourceCount: sourceArray.length, outputVariable: output_variable } }
            );
            
            setTimeout(() => moveToNextNode(node.id, newVars), 800);
            return;
          }
          
          // =====================================================================
          // Modo padrão: salva texto formatado na variável de saída
          // =====================================================================
          
          // Atualiza variáveis
          const newVars = { ...vars, [output_variable]: result };
          setVariables(newVars);
          
          log.info(`[format_array] Formatados ${sourceArray.length} itens em "${output_variable}"`);
          
          addMessage(
            `📝 Formatado: ${sourceArray.length} itens salvos em "${output_variable}"`,
            'action',
            { metadata: { sourceCount: sourceArray.length, outputVariable: output_variable } }
          );
          
          setTimeout(() => moveToNextNode(node.id, newVars), 800);
        })();
        break;

      // =========================================================================
      // Node: Link de Agendamento Online (simula geração de link no teste)
      // =========================================================================
      case 'crm_scheduling_link': {
        const schedulingConfig = config as any;
        const eventTitle = schedulingConfig?.event_type_title || 'Evento';
        const outputVar = schedulingConfig?.output_variable || 'scheduling_link';
        const slug = schedulingConfig?.event_type_slug || 'evento';
        
        // Gera link simulado para ambiente de teste
        const simulatedLink = `${window.location.origin}/schedule/test-tenant/${slug}?deal_id=test-deal-id`;
        
        // Salva na variável de saída configurada
        vars[outputVar] = simulatedLink;
        setVariables({ ...vars });
        
        addMessage(
          `Link de agendamento gerado para "${eventTitle}" → salvo em "{{${outputVar}}}"`,
          'action',
          { metadata: { outputVariable: outputVar, link: simulatedLink } }
        );
        
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;
      }

      // Node de incorporação (embed) - renderiza iframe no teste
      case 'embed': {
        let embedUrl = (node.data.config as any)?.url || '';
        const embedHeight = (node.data.config as any)?.height || 400;
        
        // Substitui variáveis na URL (ex: {{url_doc}})
        embedUrl = replaceVariables(embedUrl, vars);
        
        // Normaliza URL para formato de embed (YouTube, Vimeo, etc.)
        embedUrl = normalizeEmbedUrl(embedUrl);
        
        if (embedUrl) {
          // Adiciona mensagem do bot com metadata para renderizar iframe
          addMessage('', 'bot', { metadata: { embedUrl, embedHeight } });
        } else {
          addMessage('⚠️ Incorporar: URL não configurada', 'system');
        }
        setTimeout(() => moveToNextNode(node.id, vars), 500);
        break;
      }
      /**
       * Simulação do node contract_generate no teste de fluxo
       * Gera um contract_id fake e salva nas variáveis do fluxo
       */
      case 'contract_generate': {
        const cgConfig = config as any;
        const contractName = cgConfig?.published_contract_name || 'Contrato';
        const variableMapping = cgConfig?.variable_mapping || {};
        const signers = cgConfig?.signers || [];
        const dealSel = cgConfig?.deal_selection || 'from_trigger';

        // Gera ID simulado para o contrato
        const fakeContractId = crypto.randomUUID();
        vars['contract_id'] = fakeContractId;
        setVariables({ ...vars });

        // Conta variáveis mapeadas para exibição
        const mappedCount = Object.keys(variableMapping).length;

        // Indicador de deal na simulação
        const dealInfo = dealSel === 'none' ? '(sem negócio vinculado)' : dealSel === 'from_variable' ? `(deal via variável)` : '(deal do gatilho)';

        addMessage(
          `📄 Contrato "${contractName}" gerado (simulado)\n→ contract_id: ${fakeContractId.slice(0, 8)}...\n→ ${mappedCount} variável(is) mapeada(s)\n→ ${signers.length} signatário(s)\n→ ${dealInfo}`,
          'action'
        );

        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;
      }

      /**
       * Simulação dos nodes de Tarefa e Notificação no Testar Fluxo.
       * São apenas previews visuais — execução real é feita pelo Flow Worker.
       * RELEASE: 2026-06-23 v1.2.0 - Adicionados task_create, task_change_phase,
       *                              task_assign e send_notification.
       */
      case 'task_create': {
        const tc: any = config || {};
        // Resolve título conforme modo
        let title = '';
        if (tc.title_mode === 'from_variable' && tc.title_variable) {
          title = String(vars[tc.title_variable] ?? `{{${tc.title_variable}}}`);
        } else if (tc.title_mode === 'static' && tc.title_static) {
          title = replaceVariables(tc.title_static, vars);
        } else {
          title = 'Nova tarefa';
        }

        // Resolve responsável conforme modo
        let assigneeLabel = 'Sem responsável';
        switch (tc.assignee_mode) {
          case 'manual':
            assigneeLabel = tc.assignee_name || 'Usuário fixo';
            break;
          case 'deal_seller':
            assigneeLabel = 'Vendedor do negócio';
            break;
          case 'contact_seller':
            assigneeLabel = 'Vendedor do contato';
            break;
          case 'from_variable':
            assigneeLabel = tc.assignee_variable
              ? `{{${tc.assignee_variable}}}`
              : 'De variável';
            break;
        }

        // Gera task_id fake e propaga para nodes seguintes
        const fakeTaskId = crypto.randomUUID();
        vars['task_id'] = fakeTaskId;
        vars['task_title'] = title;
        setVariables({ ...vars });

        const categoryLabel = tc.category_name || 'Categoria';
        const phaseLabel = tc.phase_name || 'Fase';
        const priority = tc.priority || 'normal';

        addMessage(
          `✅ Tarefa criada (simulado)\n→ task_id: ${fakeTaskId.slice(0, 8).toUpperCase()}\n→ ${categoryLabel} → ${phaseLabel}\n→ Título: ${title}\n→ Responsável: ${assigneeLabel}\n→ Prioridade: ${priority}`,
          'action'
        );
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;
      }

      case 'task_change_phase': {
        const tcp: any = config || {};
        // Resolve origem da tarefa
        let taskLabel: string;
        if (tcp.task_source === 'from_variable' && tcp.task_variable) {
          const v = vars[tcp.task_variable];
          taskLabel = v ? String(v).slice(0, 8).toUpperCase() : `{{${tcp.task_variable}}}`;
        } else {
          taskLabel = vars['task_id']
            ? String(vars['task_id']).slice(0, 8).toUpperCase()
            : 'Tarefa do gatilho';
        }

        const categoryLabel = tcp.category_name || 'Categoria';
        const phaseLabel = tcp.phase_name || 'Fase';

        addMessage(
          `🔄 Fase alterada (simulado)\n→ Tarefa: ${taskLabel}\n→ Nova fase: ${categoryLabel} → ${phaseLabel}`,
          'action'
        );
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;
      }

      case 'task_assign': {
        const ta: any = config || {};
        // Resolve origem da tarefa
        let taskLabel: string;
        if (ta.task_source === 'from_variable' && ta.task_variable) {
          const v = vars[ta.task_variable];
          taskLabel = v ? String(v).slice(0, 8).toUpperCase() : `{{${ta.task_variable}}}`;
        } else {
          taskLabel = vars['task_id']
            ? String(vars['task_id']).slice(0, 8).toUpperCase()
            : 'Tarefa do gatilho';
        }

        // Resolve responsável conforme modo
        let assigneeLabel = '—';
        switch (ta.assignee_mode) {
          case 'manual':
            assigneeLabel = `Atribuído a ${ta.assignee_name || 'usuário fixo'}`;
            break;
          case 'deal_seller':
            assigneeLabel = 'Atribuído ao vendedor do negócio';
            break;
          case 'from_variable':
            assigneeLabel = `Atribuído via {{${ta.assignee_variable || 'variavel'}}}`;
            break;
          case 'clear':
            assigneeLabel = 'Responsável removido';
            break;
        }

        addMessage(
          `👤 Responsável atualizado (simulado)\n→ Tarefa: ${taskLabel}\n→ ${assigneeLabel}`,
          'action'
        );
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;
      }

      case 'send_notification': {
        const sn = (config || {}) as SendNotificationConfig;
        // Normaliza destinatários (suporta v1 e v2 do schema)
        const recipients = normalizeNotificationRecipients(sn);

        // Monta label legível para cada destinatário
        const recipientLabels = recipients.map((r) => {
          switch (r.mode) {
            case 'system_user':
              return r.user_name || 'Usuário';
            case 'crm_seller':
              return r.seller_name || 'Vendedor CRM';
            case 'deal_seller':
              return 'Vendedor do negócio';
            case 'variable':
              return r.variable ? `{{${r.variable}}}` : 'Variável';
            case 'fixed':
              return r.phone || 'Telefone';
            default:
              return r.mode || 'Destinatário';
          }
        });

        const inboxLabel = sn.inbox_name || 'Inbox';
        const rawMsg = replaceVariables(sn.message_text || '', vars);
        const preview = rawMsg.length > 100 ? `${rawMsg.slice(0, 100)}...` : rawMsg;
        const toList = recipientLabels.length > 0 ? recipientLabels.join(', ') : '—';

        addMessage(
          `🔔 Notificação enviada (simulado) via ${inboxLabel}\n→ Para: ${toList}\n→ Mensagem: "${preview}"`,
          'action'
        );
        setTimeout(() => moveToNextNode(node.id, vars), 800);
        break;
      }

      default:
        addMessage(`Node não implementado: ${node.data.type}`, 'system');
        setTimeout(() => moveToNextNode(node.id, vars), 1000);
    }
  }, [addMessage, replaceVariables, evaluateCondition, moveToNextNode, evaluateComparison]);

  // Atualiza ref para ser acessível recursivamente
  useEffect(() => {
    processTestNodeRef.current = processTestNode;
  }, [processTestNode]);


  /**
   * Inicia teste do fluxo
   * @param flowNodes - Nodes do fluxo
   * @param flowEdges - Edges do fluxo
   * @param startFromNodeId - (Opcional) ID do node a partir do qual iniciar o teste
   */
  const startTest = useCallback((flowNodes: FlowNode[], flowEdges: FlowEdge[], startFromNodeId?: string) => {
    log.info('Iniciando teste de fluxo', { startFromNodeId: startFromNodeId || 'start' });
    setNodes(flowNodes);
    setEdges(flowEdges);
    setMessages([]);
    setVariables({});
    setIsRunning(true);
    setIsWaitingInput(false);

    // Atualiza refs imediatamente
    nodesRef.current = flowNodes;
    edgesRef.current = flowEdges;

    // Se startFromNodeId fornecido, inicia a partir desse node
    if (startFromNodeId) {
      const targetNode = flowNodes.find(n => n.id === startFromNodeId);
      if (targetNode) {
        const nickname = targetNode.data.config?.nickname || targetNode.data.type;
        addMessage(`▶ Teste iniciado a partir do node: ${nickname}`, 'system');
        processTestNodeRef.current?.(targetNode, {});
      } else {
        addMessage('Erro: Node de início não encontrado', 'system');
        setIsRunning(false);
      }
      return;
    }

    // Comportamento padrão: encontra node de início
    const startNode = flowNodes.find(n => n.data.type === 'start');
    if (startNode) {
      processTestNodeRef.current?.(startNode, {});
    } else {
      addMessage('Erro: Nenhum node de início encontrado', 'system');
      setIsRunning(false);
    }
  }, [addMessage]);

  /**
   * Submete resposta do usuário
   */
  const submitResponse = useCallback((value: string) => {
    if (!currentNodeId || !isWaitingInput) return;

    const currentNode = nodesRef.current.find(n => n.id === currentNodeId);
    if (!currentNode) return;

    const config = currentNode.data.config || {};

    // Validação de input_data antes de aceitar
    if (currentNode.data.type === 'input_data' && config.data_type) {
      const validation = validateInput(
        value,
        config.data_type,
        config.validation_message
      );

      if (!validation.isValid) {
        // Mostra mensagem de erro e mantém aguardando input
        addMessage(value, 'user');
        addMessage(`❌ ${validation.error}`, 'bot');
        addMessage(config.question || 'Digite sua resposta:', 'bot');
        log.debug('Validação falhou', { value, dataType: config.data_type, error: validation.error });
        return; // Não avança, aguarda nova resposta
      }
    }

    addMessage(value, 'user');
    setIsWaitingInput(false);

    const newVars = { ...variablesRef.current };

    // Salva variável baseado no tipo de node
    if (currentNode.data.type === 'input_data' && config.variable_name) {
      newVars[config.variable_name] = value;
      setVariables(newVars);
      log.debug('Variável salva', { variable: config.variable_name, value });
    } else if ((currentNode.data.type === 'question_buttons' || currentNode.data.type === 'question_list') && config.variable_name) {
      newVars[config.variable_name] = value;
      setVariables(newVars);
      log.debug('Variável salva', { variable: config.variable_name, value });
    }

    // Para perguntas com opções, encontrar o sourceHandle correto
    let sourceHandle: string | undefined;
    
    if (currentNode.data.type === 'question_buttons' || currentNode.data.type === 'question_list') {
      const options = config.options || [];
      // Encontra o índice da opção selecionada pelo texto (label)
      const selectedIndex = options.findIndex((opt: any) => 
        (opt.label || opt.title) === value
      );
      
      if (selectedIndex >= 0) {
        sourceHandle = `option_${selectedIndex}`;
        log.debug('Opção selecionada', { value, selectedIndex, sourceHandle });
      }
    }

    // Move para próximo node usando o sourceHandle específico
    setTimeout(() => moveToNextNode(currentNodeId, newVars, sourceHandle), 500);
  }, [currentNodeId, isWaitingInput, addMessage, moveToNextNode]);

  /**
   * Para teste
   */
  const stopTest = useCallback(() => {
    log.info('Parando teste de fluxo');
    setIsRunning(false);
    setIsWaitingInput(false);
    setHighlightedNodeId(null);
    addMessage('Teste interrompido', 'system');
  }, [addMessage]);

  /**
   * Reinicia teste
   */
  const restartTest = useCallback(() => {
    log.info('Reiniciando teste de fluxo');
    if (nodes.length > 0 && edges.length > 0) {
      startTest(nodes, edges);
    }
  }, [nodes, edges, startTest]);

  return {
    messages,
    variables,
    currentNodeId,
    nodes,
    isWaitingInput,
    isRunning,
    highlightedNodeId,
    startTest,
    stopTest,
    restartTest,
    submitResponse,
  };
}
